import React, { FC, useMemo } from 'react';

import { useTranslation } from '@modernloop/i18n';
import { getPrimaryAccentTextGradient } from '@modernloop/shared/theme';
import { IconButton, Stack, Theme, Typography, keyframes } from '@mui/material';

import { SourceType, useTaskStopAgentMutation } from 'src/generated/mloop-graphql';

type Props = {
  sourceId: string;
  sourceType: SourceType;
  delegateStatus?: string | null;
  aiAgentLabel: string;
};

const shimmerText = keyframes`
  0% {
    background-position: 200% center;
  }
  100% {
    background-position: -200% center;
  }
`;

const ActivityFeedAgentStatus: FC<Props> = ({ sourceId, sourceType, delegateStatus, aiAgentLabel }) => {
  const { t } = useTranslation();
  const [stopAgent, { loading: stoppingAgent }] = useTaskStopAgentMutation();

  const agentStatusText = useMemo(() => {
    if (sourceType !== SourceType.SourceTypeTask) {
      return null;
    }
    if (delegateStatus !== 'ACTIVE') {
      return null;
    }

    return t('hiring-dashboard-common:common.activityFeed.agentWorking', { agentLabel: aiAgentLabel });
  }, [aiAgentLabel, delegateStatus, sourceType, t]);

  const handleStopAgent = async () => {
    if (!agentStatusText || sourceType !== SourceType.SourceTypeTask) {
      return;
    }

    try {
      await stopAgent({
        variables: { input: { taskId: sourceId } },
        optimisticResponse: {
          taskStopAgent: {
            task: { id: sourceId, delegateType: 'AGENT', delegateStatus: 'STOPPED', __typename: 'Task' },
            __typename: 'TaskStopAgentOutput',
          },
        },
      });
    } catch {
      // Mutation errors are surfaced by Apollo; no local fallback UI here.
    }
  };

  if (!agentStatusText) {
    return null;
  }

  return (
    <Stack
      direction="row"
      alignItems="center"
      justifyContent="center"
      spacing={0.5}
      sx={{
        px: 1,
        pt: 0,
        pb: 0.5,
        mt: 0,
      }}
    >
      <Typography
        variant="caption"
        fontWeight={600}
        sx={{
          textAlign: 'center',
          backgroundImage: (theme: Theme) => getPrimaryAccentTextGradient(theme),
          backgroundSize: '200% auto',
          backgroundClip: 'text',
          color: 'transparent',
          animation: `${shimmerText} 2.2s linear infinite`,
          '@media (prefers-reduced-motion: reduce)': {
            animation: 'none',
          },
        }}
      >
        {agentStatusText}
      </Typography>
      <IconButton
        aria-label={t('hiring-dashboard-common:common.activityFeed.stopAgent')}
        disabled={stoppingAgent}
        onClick={handleStopAgent}
        size="small"
        sx={{
          color: 'text.secondary',
          p: 0.25,
        }}
      >
        <Typography component="span" variant="caption" fontWeight={700} lineHeight={1}>
          ×
        </Typography>
      </IconButton>
    </Stack>
  );
};

export default ActivityFeedAgentStatus;
